import { useCallback, useEffect, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import {
  Edit as EditIcon,
  Store as StoreIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, statusColors, statusLabels, getErrorMessage } from "@/features/shared";
import { SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const Users = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("users", {
        params: {
          ...paginationParams,
          ...(keyword && { search: keyword }),
          withRole: true,
        },
      });
      setUsers(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat pengguna"));
    } finally {
      setLoading(false);
    }
  }, [paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const toolbarContent = hasPermission(PermissionEnum.CREATE_USERS) && (
        <Button variant="contained" component={Link} to="create">
          Tambah
        </Button>
      );

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]} slotProps={{ header: { slotProps: { toolbar: { children: toolbarContent } } } }}>
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nama/username"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={users}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 3,
              sortable: false,
            },
            {
              field: "username",
              headerName: "Username",
              flex: 3,
              sortable: false,
            },
            {
              field: "role",
              headerName: "Role",
              flex: 3,
              sortable: false,
              valueGetter: (value) => value.name,
            },
            {
              field: "status",
              headerName: "Status",
              flex: 2,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              flex: 2,
              minWidth: 120,
              getActions: (params) => {
                const actions = [];
                actions.push(
                  <IconButton
                    key="view"
                    onClick={() => navigate(params.id)}
                    size="small"
                  >
                    <VisibilityIcon />
                  </IconButton>
                );
                if (hasPermission(PermissionEnum.UPDATE_USERS)) {
                  actions.push(
                    <IconButton
                      key="edit"
                      onClick={() => navigate(`${params.id}/edit`)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  );
                }
                if (hasPermission(PermissionEnum.READ_USER_OUTLET)) {
                  actions.push(
                    <IconButton
                      key="outlets"
                      onClick={() => navigate(`${params.id}/outlets`)}
                      size="small"
                    >
                      <StoreIcon />
                    </IconButton>
                  );
                }
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
